import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  IconButton,
  Autocomplete,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel
} from "@mui/material";
import { MdEdit, MdDelete, MdOutlineModeEdit, MdDeleteOutline, MdContentCopy } from "react-icons/md";
import { useOutletContext } from "react-router-dom";
import "../../../styles/admin.css/machine-modifications-css/manage-cars.css";
import {
  useGetOilRequirementsQuery,
  useGetUniqueBrandsQuery,
  useGetModelsByBrandQuery,
  useCreateOilRequirementMutation,
  useUpdateOilRequirementMutation,
  useDeleteOilRequirementMutation
} from "../../../redux/slices/admin/oilRequirement/oilRequirementSlice.js";

export default function OilRequirements() {
  const { sidebarOpen } = useOutletContext();
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;

  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [keepOpen, setKeepOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    yearStart: '',
    yearEnd: '',
    engine: '',
    oilType: '',
    liters: ''
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [filters, setFilters] = useState({
    brand: '',
    model: '',
    yearStart: '',
    yearEnd: '',
    engine: '',
    oilType: '',
    liters: ''
  });

  const { data: requirementsData, isLoading, refetch } = useGetOilRequirementsQuery({
    page,
    limit: itemsPerPage,
    ...filters
  });

  const { data: brands } = useGetUniqueBrandsQuery();
  const { data: models } = useGetModelsByBrandQuery(formData.brand, { skip: !formData.brand });

  const [createOilRequirement] = useCreateOilRequirementMutation();
  const [updateOilRequirement] = useUpdateOilRequirementMutation();
  const [deleteOilRequirement] = useDeleteOilRequirementMutation();

  const requirements = requirementsData?.data || [];
  const pagination = requirementsData?.pagination || { total: 0, totalPages: 0 };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditMode(true);
      setSelectedId(item.id);
      setFormData({
        brand: item.brand,
        model: item.model,
        yearStart: item.yearStart,
        yearEnd: item.yearEnd,
        engine: item.engine,
        oilType: item.oilType,
        liters: item.liters
      });
    } else {
      setEditMode(false);
      setSelectedId(null);
      setFormData({
        brand: '',
        model: '',
        yearStart: '',
        yearEnd: '',
        engine: '',
        oilType: '',
        liters: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      brand: '',
      model: '',
      yearStart: '',
      yearEnd: '',
      engine: '',
      oilType: '',
      liters: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await updateOilRequirement({ id: selectedId, ...formData }).unwrap();
        setSnackbar({ open: true, message: 'Məlumat uğurla yeniləndi', severity: 'success' });
        handleCloseDialog();
      } else {
        await createOilRequirement(formData).unwrap();
        setSnackbar({ open: true, message: 'Məlumat uğurla əlavə edildi', severity: 'success' });
        if (keepOpen) {
          setFormData({
            brand: '',
            model: '',
            yearStart: '',
            yearEnd: '',
            engine: '',
            oilType: '',
            liters: ''
          });
        } else {
          handleCloseDialog();
        }
      }
      refetch();
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'Xəta baş verdi', severity: 'error' });
    }
  };

  const handleDelete = async (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDuplicate = (item) => {
    setFormData({
      brand: item.brand,
      model: item.model,
      yearStart: item.yearStart,
      yearEnd: item.yearEnd,
      engine: item.engine,
      oilType: item.oilType,
      liters: item.liters
    });
    setEditMode(false);
    setSelectedId(null);
    setKeepOpen(false);
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteOilRequirement(deleteItem.id).unwrap();
      setSnackbar({ open: true, message: 'Məlumat uğurla silindi', severity: 'success' });
      setDeleteDialogOpen(false);
      setDeleteItem(null);
      refetch();
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'Xəta baş verdi', severity: 'error' });
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const totalPages = Math.ceil(pagination.total / itemsPerPage);

  return (
    <Box className={`total-payments-container ${sidebarOpen ? "sidebar-open" : ""}`}>
      <Box className="total-payments-header">
        <Box className="total-payments-title">
          <Typography style={{ color: "#545A61" }}>Yağ Tələbləri</Typography>
          <Divider className="total-payments-divider" />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, marginTop: 20, flexWrap: 'wrap' }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)', xl: 'repeat(7, 1fr)' },
            gap: 1.5,
            alignItems: 'center',
            flex: 1
          }}>
            <TextField
              size="small"
              label="Marka"
              value={filters.brand}
              onChange={(e) => handleFilterChange('brand', e.target.value)}
              fullWidth
            />

            <TextField
              size="small"
              label="Model"
              value={filters.model}
              onChange={(e) => handleFilterChange('model', e.target.value)}
              fullWidth
            />

            <TextField
              size="small"
              label="İl Başlanğıc"
              type="number"
              value={filters.yearStart}
              onChange={(e) => handleFilterChange('yearStart', e.target.value)}
              fullWidth
            />

            <TextField
              size="small"
              label="İl Bitiş"
              type="number"
              value={filters.yearEnd}
              onChange={(e) => handleFilterChange('yearEnd', e.target.value)}
              fullWidth
            />

            <TextField
              size="small"
              label="Motor"
              value={filters.engine}
              onChange={(e) => handleFilterChange('engine', e.target.value)}
              fullWidth
            />

            <TextField
              size="small"
              label="Yağ Növü"
              value={filters.oilType}
              onChange={(e) => handleFilterChange('oilType', e.target.value)}
              fullWidth
            />

            <TextField
              size="small"
              label="Litr"
              type="number"
              value={filters.liters}
              onChange={(e) => handleFilterChange('liters', e.target.value)}
              fullWidth
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={() => setFilters({ brand: '', model: '', yearStart: '', yearEnd: '', engine: '', oilType: '', liters: '' })}
              sx={{ minWidth: 120 }}
            >
              Təmizlə
            </Button>
            <Button
              variant="contained"
              onClick={() => handleOpenDialog()}
              sx={{ minWidth: 120, backgroundColor: '#3B7DDD' }}
            >
              Əlavə Et
            </Button>
          </Box>
        </Box>
      </Box>

      <TableContainer component={Paper} className="total-payments-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>#No</strong></TableCell>
              <TableCell><strong>Marka</strong></TableCell>
              <TableCell><strong>Model</strong></TableCell>
              <TableCell><strong>İl</strong></TableCell>
              <TableCell><strong>Motor</strong></TableCell>
              <TableCell><strong>Yağ Tələbi</strong></TableCell>
              <TableCell><strong>Litr</strong></TableCell>
              <TableCell><strong>Əməliyyatlar</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Yüklənir...</TableCell>
              </TableRow>
            ) : requirements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Məlumat tapılmadı</TableCell>
              </TableRow>
            ) : (
              requirements.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell>{"#" + String(item.serialNo || item.id).padStart(4, "0")}</TableCell>
                  <TableCell>{item.brand}</TableCell>
                  <TableCell>{item.model}</TableCell>
                  <TableCell>{item.yearStart} - {item.yearEnd}</TableCell>
                  <TableCell>{item.engine}</TableCell>
                  <TableCell>{item.oilType}</TableCell>
                  <TableCell>{item.liters}</TableCell>
                  <TableCell className="manage-wrapper">
                    <Button
                      variant="contained"
                      size="small"
                      className="btn-edit"
                      onClick={() => handleOpenDialog(item)}
                    >
                      <MdOutlineModeEdit style={{ width: "24px", height: "24px" }} />
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleDuplicate(item)}
                      sx={{ backgroundColor: '#4CAF50' }}
                    >
                      <MdContentCopy style={{ width: "24px", height: "24px" }} />
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      className="btnn-delete"
                      onClick={() => handleDelete(item)}
                    >
                      <MdDeleteOutline style={{ width: "24px", height: "24px" }} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'right', p: 3, mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => {
              setPage(value);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Düzəlt' : 'Əlavə Et'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <Autocomplete
              freeSolo
              options={Array.isArray(brands) ? brands : []}
              value={formData.brand}
              onChange={(e, newValue) => setFormData({ ...formData, brand: newValue || '' })}
              onInputChange={(e, newValue) => setFormData({ ...formData, brand: newValue || '' })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Marka"
                  fullWidth
                />
              )}
            />

            <Autocomplete
              freeSolo
              options={Array.isArray(models) ? models : []}
              value={formData.model}
              onChange={(e, newValue) => setFormData({ ...formData, model: newValue || '' })}
              onInputChange={(e, newValue) => setFormData({ ...formData, model: newValue || '' })}
              disabled={!formData.brand}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Model"
                  fullWidth
                />
              )}
            />

            <FormControl fullWidth size="small">
              <TextField
                size="small"
                label="İl Başlanğıc"
                type="number"
                value={formData.yearStart}
                onChange={(e) => setFormData({ ...formData, yearStart: e.target.value.replace(/[^0-9]/g, '') })}
                onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
              />
            </FormControl>

            <FormControl fullWidth size="small">
              <TextField
                size="small"
                label="İl Bitiş"
                type="number"
                value={formData.yearEnd}
                onChange={(e) => setFormData({ ...formData, yearEnd: e.target.value.replace(/[^0-9]/g, '') })}
                onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
              />
            </FormControl>

            <FormControl fullWidth size="small">
              <TextField
                size="small"
                label="Motor"
                value={formData.engine}
                onChange={(e) => setFormData({ ...formData, engine: e.target.value })}
              />
            </FormControl>

            <FormControl fullWidth size="small">
              <TextField
                size="small"
                label="Yağ Növü"
                value={formData.oilType}
                onChange={(e) => setFormData({ ...formData, oilType: e.target.value })}
              />
            </FormControl>

            <FormControl fullWidth size="small">
              <TextField
                size="small"
                label="Litr"
                type="number"
                value={formData.liters}
                onChange={(e) => setFormData({ ...formData, liters: e.target.value })}
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Ləğv et</Button>
          {!editMode && (
            <FormControlLabel
              control={
                <Switch
                  checked={keepOpen}
                  onChange={(e) => setKeepOpen(e.target.checked)}
                  size="small"
                />
              }
              label="Açıq qalsın"
            />
          )}
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editMode ? 'Yenilə' : 'Əlavə Et'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Silmək istədiyinizdən əminsiniz?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Bu əməliyyatı geri qaytarmaq mümkün olmayacaq.
          </Typography>
          {deleteItem && (
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'grey.200' }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Marka:</strong></TableCell>
                    <TableCell>{deleteItem.brand}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Model:</strong></TableCell>
                    <TableCell>{deleteItem.model}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>İl Aralığı:</strong></TableCell>
                    <TableCell>{deleteItem.yearStart} - {deleteItem.yearEnd}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Motor:</strong></TableCell>
                    <TableCell>{deleteItem.engine}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Yağ Növü:</strong></TableCell>
                    <TableCell>{deleteItem.oilType}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Litr:</strong></TableCell>
                    <TableCell>{deleteItem.liters}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Ləğv et</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
